import classNames from 'clsx';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import type { Market } from 'widgets/sports-competition-outrights-widget/types';

import Translation from '@swf/components/Translation/Translation';
import { select } from '@swf/core/store';
import type { EventId } from '@swf/generated/types/api/sports';
import { MarketStatus, RunnerStatusMarketDetails } from '@swf/generated/types/api/sports';
import { Selection } from '@swf/sports/components';
import RunnerDetails from '@swf/sports/components/MarketTemplate/components/RunnerDetails/RunnerDetails';

import { TXTWCL_Show_More } from './Default.messages.json';
import styles from './Default.scss';

export type Props = Market & {
  eventId: EventId;
  eventStartTime: string;
  showPreviousPrice?: boolean;
};

const SHOW_MORE_THRESHOLD = 14;
const SHOW_MORE_CAP = 10;

export const Default: React.FC<Props> = ({
  runners = [],
  marketStatus,
  id: marketId,
  eventId,
  legTypes,
  eventStartTime,
  showPreviousPrice = false
}) => {
  const [showAll, setShowAll] = useState(false);
  const urlParams = useSelector(select.sports.getUrlParams);
  const limitRunners = !showAll && runners.length >= SHOW_MORE_THRESHOLD;
  const runnersToDisplay = limitRunners ? runners.slice(0, SHOW_MORE_CAP) : runners;

  return (
    <div data-testid="market-template-default">
      {runnersToDisplay.map((runner) => {
        const labelClassName = classNames(styles.label, {
          [styles.disabled]:
            marketStatus !== MarketStatus.OPEN ||
            runner.runnerStatus !== RunnerStatusMarketDetails.ACTIVE
        });

        return (
          <RunnerDetails
            key={runner.selectionId}
            runner={runner}
            labelClassName={labelClassName}
            showPreviousPrice={showPreviousPrice}
          >
            <Selection
              className={styles.odds}
              competitionId={Number(urlParams.competitionId)}
              eventId={eventId}
              eventTypeId={Number(urlParams.eventTypeId)}
              handicap={runner.handicap}
              legTypes={legTypes}
              marketId={marketId}
              marketStatus={marketStatus}
              odds={runner.winRunnerOdds}
              runnerResultType={runner.resultType}
              runnerStatus={runner.runnerStatus}
              selectionId={runner.selectionId}
              startTime={eventStartTime}
            />
          </RunnerDetails>
        );
      })}

      {limitRunners && (
        <div className={styles.showMoreWrapper}>
          <button type="button" className={styles.showMore} onClick={() => setShowAll(true)}>
            <Translation {...TXTWCL_Show_More} />
          </button>
        </div>
      )}
    </div>
  );
};
